// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useEffect } from 'react';

import type { UnitOfMeasure } from '@evolvemep/api-ts';
import { Flex, Text, TextInput, getDefaultZIndex } from '@mantine/core';
import { useForm } from '@mantine/form';

import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { useWrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { type NewUnitOfMeasureBody, useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';

type Props = {
  defaultValue?: string;
  opened: boolean;
  onClose: () => void;
  onAdded: (newUOM: UnitOfMeasure) => void;
};

const NewUnitOfMeasureForm = ({ defaultValue, opened, onAdded }: Props) => {
  const { setDisabled } = useWrappedModal();
  const { uoms, addNewUOM } = useUnitOfMeasure();
  const [saving, setSaving] = useState(false);
  const form = useForm<NewUnitOfMeasureBody>({
    initialValues: {
      name: defaultValue,
      code: '',
      unitOfMeasureTypeId: '',
    } as NewUnitOfMeasureBody,
    initialDirty: {
      name: true,
    },
    validate: {
      name: (n) => !n,
      code: (c) => !c,
    },
  });

  const onSubmit = async (values: NewUnitOfMeasureBody) => {
    setSaving(true);
    await addNewUOM(values)
      .then(onAdded)
      .finally(() => setSaving(false));
  };

  useFormModal(form, onSubmit);

  const shouldErrorCheck = opened && !saving;
  const uomNameExists = shouldErrorCheck && uoms?.some((u) => form.values.name === u.name);
  const uomCodeExists = shouldErrorCheck && uoms?.some((u) => form.values.code === u.code);

  useEffect(() => {
    setDisabled((uomNameExists || uomCodeExists) ?? false);
  }, [setDisabled, uomCodeExists, uomNameExists]);

  return (
    <>
      <Flex gap="md">
        <TextInput
          label="UOM Name"
          style={{ width: '100%' }}
          maxLength={32}
          required
          autoFocus={!defaultValue}
          data-autofocus={!defaultValue}
          {...form.getInputProps('name')}
          error={uomNameExists}
        />
        <TextInput
          label="Code"
          autoFocus={!!defaultValue}
          data-autofocus={!!defaultValue}
          style={{ width: 110 }}
          maxLength={32}
          required
          {...form.getInputProps('code')}
          error={uomCodeExists}
        />
      </Flex>
      {uomNameExists && (
        <Text fz="sm" c="red">
          UOM <b>{form.values.name}</b> already exists.
        </Text>
      )}
      {uomCodeExists && (
        <Text fz="sm" c="red">
          UOM Code <b>{form.values.code}</b> already exists.
        </Text>
      )}
    </>
  );
};

export const NewUnitOfMeasureModal = ({ ...props }: Props) => {
  // This modal is typically used inside a Popover,
  // so we need to have a z-index higher than the Popover
  const popoverZIndex = getDefaultZIndex('popover');
  return (
    <FormModal
      title="New UOM"
      opened={props.opened}
      onClose={props.onClose}
      zIndex={popoverZIndex + 2}
      overlayProps={{ zIndex: popoverZIndex + 1 }}
      type="submit"
      confirmationText="Create"
      size="sm"
    >
      <NewUnitOfMeasureForm {...props} />
    </FormModal>
  );
};
